import { useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { useAlert } from "contexts/alert";
import { useAuth } from "contexts/auth";
import { formatCnpj } from "utils/federal-tax-id";

import { alcanceDoConvite, podeMudarAlcance, podeMudarBalanco, raizInteira } from "./alcance";
import { NOME_DO_PAPEL, papelLegivel, TEXTOS } from "./frases";
import { ErroDeAcesso, gestaoDeUsuarios } from "./gestao-de-usuarios.service";
import {
  IAlcanceDoVinculo,
  IClienteDaCarteira,
  IConvitePendente,
  IUsuarioDaOrganizacao,
  Lado,
} from "./gestao-de-usuarios.types";
import { ModalDeAlcance } from "./ModalDeAlcance";
import { ModalDeConfirmacao, ModalDeConvite, ModalDePapeis, ModalDeRevisao } from "./Modais";

export interface ITabelaDeUsuariosProps {
  organizationId: string;
  organizacaoNome: string;
  lado: Lado;
  /** Ações de papel, convite, remoção e suspensão (revista e sem Gestão própria alheia). */
  editavel: boolean;
  /** `eu.podeGerir`: base do botão Alcance (só existe na corretora). */
  podeGerir: boolean;
  /** Botão Confirmar administradores. */
  podeRevisar: boolean;
  /** Aviso no topo (não revista, ou Gestão própria da cliente). */
  aviso?: string;
}

type Modal =
  | { tipo: "convite" }
  | { tipo: "revisao" }
  | { tipo: "papeis"; usuario: IUsuarioDaOrganizacao }
  | { tipo: "remover"; usuario: IUsuarioDaOrganizacao }
  | { tipo: "suspender"; usuario: IUsuarioDaOrganizacao }
  | {
      tipo: "alcance";
      usuario: IUsuarioDaOrganizacao;
      atual: IAlcanceDoVinculo;
      carteira: IClienteDaCarteira[];
    }
  | { tipo: "alcanceDoConvite"; convite: IConvitePendente; atual: IAlcanceDoVinculo; carteira: IClienteDaCarteira[] }
  | { tipo: "papeisDoConvite"; convite: IConvitePendente }
  | { tipo: "cancelarConvite"; convite: IConvitePendente }
  | null;

const alcanceLegivel = (alcance: string, lado: Lado) =>
  alcance === "ORGANIZACAO" && lado === "CORRETORA" ? TEXTOS.TODA_A_CARTEIRA : TEXTOS.ESTA_ORGANIZACAO;

export function TabelaDeUsuarios({
  organizationId,
  organizacaoNome,
  lado,
  editavel,
  podeGerir,
  podeRevisar,
  aviso,
}: ITabelaDeUsuariosProps) {
  const { openAlert } = useAlert();
  const cliente = useQueryClient();
  const { user } = useAuth();
  const [modal, setModal] = useState<Modal>(null);
  const [ocupado, setOcupado] = useState(false);
  const {
    data: usuarios,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["acesso", "usuarios", organizationId],
    gcTime: 0,
    queryFn: () => gestaoDeUsuarios.usuarios(organizationId),
  });
  // why: quem foi convidado e ainda não aceitou não está em usuarios (19/10); sem isto some da tela.
  const { data: convites, isError: convitesComErro } = useQuery({
    queryKey: ["acesso", "convites", organizationId],
    gcTime: 0,
    queryFn: () => gestaoDeUsuarios.convites(organizationId),
  });

  // why: um GET por vínculo SELECAO (decisão 4 do plano B); B1.4 lê a mesma chave.
  const chaveDoAlcance = (id: string) => ["acesso", "alcance", id];
  const selecionados = lado === "CORRETORA" ? (usuarios ?? []).filter((u) => u.alcance === "SELECAO") : [];
  const alcances = useQueries({
    queries: selecionados.map((u) => ({
      queryKey: chaveDoAlcance(u.organizationUserId),
      gcTime: 0,
      retry: false,
      queryFn: () => gestaoDeUsuarios.alcance(u.organizationUserId),
    })),
  });
  // why: decisões pendentes (B1.4): quem é SELECAO e tem empresa nova na carteira sem decisão.
  const pendencias = selecionados.flatMap((u, i) =>
    (alcances[i]?.data?.pendentes ?? []).map((organizationId) => ({ u, organizationId, alcance: alcances[i].data! })),
  );
  const { data: carteiraLida } = useQuery({
    queryKey: ["acesso", "carteira"],
    gcTime: 0,
    enabled: pendencias.length > 0,
    queryFn: gestaoDeUsuarios.carteira,
  });

  const lerCarteira = () =>
    cliente.fetchQuery({ queryKey: ["acesso", "carteira"], gcTime: 0, queryFn: gestaoDeUsuarios.carteira });

  async function abrirAlcanceDoConvite(c: IConvitePendente) {
    try {
      const carteira = await lerCarteira();
      setModal({ tipo: "alcanceDoConvite", convite: c, atual: alcanceDoConvite(c, carteira, organizationId), carteira });
    } catch (erro) {
      openAlert(erro instanceof ErroDeAcesso ? erro.message : TEXTOS.ERRO_AO_CARREGAR, "error");
    }
  }

  // why: sem o GET não se abre o modal: com dados vazios, a passagem para SELECAO gravaria
  // NAO_INCLUIDA para a carteira toda.
  async function abrirAlcance(u: IUsuarioDaOrganizacao) {
    try {
      const [atual, carteira] = await Promise.all([
        cliente.fetchQuery({
          queryKey: chaveDoAlcance(u.organizationUserId),
          staleTime: 0,
          gcTime: 0,
          queryFn: () => gestaoDeUsuarios.alcance(u.organizationUserId),
        }),
        lerCarteira(),
      ]);
      setModal({ tipo: "alcance", usuario: u, atual, carteira });
    } catch (erro) {
      openAlert(erro instanceof ErroDeAcesso ? erro.message : TEXTOS.ERRO_AO_CARREGAR, "error");
    }
  }

  // why: só recarrega o que mudou de fato; em recusa (403, 409) a lista fica como está e a frase
  // do backend aparece tal qual (plano 11 T8, caso 7).
  async function executar(passos: (() => Promise<unknown>)[], sucesso: string | (() => string)) {
    setOcupado(true);
    let mudou = false;
    try {
      for (const passo of passos) {
        // eslint-disable-next-line no-await-in-loop -- em ordem: um 409 para o resto
        await passo();
        mudou = true;
      }
      openAlert(typeof sucesso === "function" ? sucesso() : sucesso, "success");
      setModal(null);
    } catch (erro) {
      const motivo = erro instanceof ErroDeAcesso ? erro.message : TEXTOS.ERRO_AO_SALVAR;
      // why: num passo do meio, o que veio antes já está gravado; a tela diz e fecha o modal
      // (o modal guarda a pessoa de antes e repetir daria "Esta pessoa já tem este papel.").
      if (mudou) setModal(null);
      openAlert(mudou ? TEXTOS.PARTE_SALVA(motivo) : motivo, "error");
    } finally {
      setOcupado(false);
      if (mudou) await cliente.invalidateQueries({ queryKey: ["acesso"] });
    }
  }

  if (isLoading) return <CircularProgress />;
  if (isError || !usuarios) return <Alert severity="error">{TEXTOS.ERRO_AO_CARREGAR}</Alert>;

  const minhaLinha = usuarios.find((x) => x.userId === user?.id);
  const podeAlcance = (u: IUsuarioDaOrganizacao) => podeMudarAlcance({ lado, podeGerir, minhaLinha }, u);
  // why: mesma base de podeMudarAlcance, sem a cláusula "outra pessoa": o convite ainda não é ninguém.
  const podeAlcanceDeConvite = lado === "CORRETORA" && podeGerir && minhaLinha?.alcance === "ORGANIZACAO";
  const comAcoesDeConvite = editavel || podeAlcanceDeConvite;
  // why: o backend manda null enquanto a corretora não liga o Balanço por pessoa; sem isso a tabela fica como era.
  const balancoHabilitado = lado === "CORRETORA" && usuarios.some((u) => u.recebeBalanco !== null);
  const comAcoes = editavel || usuarios.some(podeAlcance);
  const alcanceDaLinha = (u: IUsuarioDaOrganizacao) => {
    if (lado !== "CORRETORA" || u.alcance !== "SELECAO") return alcanceLegivel(u.alcance, lado);
    const q = alcances[selecionados.findIndex((s) => s.organizationUserId === u.organizationUserId)];
    if (q?.isError) return TEXTOS.EMPRESAS_MARCADAS;
    return q?.data ? TEXTOS.N_EMPRESAS(q.data.incluidas.length) : "…";
  };

  return (
    <Box>
      {aviso && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {aviso}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {editavel && (
          <Button variant="contained" onClick={() => setModal({ tipo: "convite" })}>
            Convidar
          </Button>
        )}
        {podeRevisar && (
          <Button variant="outlined" onClick={() => setModal({ tipo: "revisao" })}>
            Confirmar administradores
          </Button>
        )}
      </Stack>
      <Table size="small" aria-label={`Usuários de ${organizacaoNome}`}>
        <TableHead>
          <TableRow>
            <TableCell>Nome</TableCell>
            <TableCell>E-mail</TableCell>
            <TableCell>Papéis</TableCell>
            <TableCell>Alcance</TableCell>
            {balancoHabilitado && <TableCell>{TEXTOS.BALANCO_DO_DIA}</TableCell>}
            {comAcoes && <TableCell>Ações</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {usuarios.map((u) => (
            <TableRow key={u.organizationUserId}>
              <TableCell>
                {u.nome}
                {lado === "EMPRESA" && u.ehStaff && (
                  <Chip size="small" label={TEXTOS.EQUIPE_DA_CORRETORA} sx={{ ml: 1 }} />
                )}
                {u.suspenso && <Chip size="small" color="warning" label={TEXTOS.SUSPENSO} sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell>
                <Chip size="small" label={NOME_DO_PAPEL.CONSULTA} sx={{ mr: 0.5 }} />
                {u.papeis
                  .filter((p) => !p.endsWith("_CONSULTA"))
                  .map((p) => (
                    <Chip key={p} size="small" color="primary" label={papelLegivel(p)} sx={{ mr: 0.5 }} />
                  ))}
              </TableCell>
              <TableCell>{alcanceDaLinha(u)}</TableCell>
              {balancoHabilitado && (
                <TableCell>
                  <Switch
                    size="small"
                    checked={!!u.recebeBalanco}
                    disabled={ocupado || !podeMudarBalanco({ lado, podeGerir, minhaLinha }, u, user?.id)}
                    inputProps={{ "aria-label": TEXTOS.BALANCO_DE(u.nome) }}
                    onChange={() => {
                      const novo = !u.recebeBalanco;
                      if (u.userId === user?.id)
                        executar(
                          [() => gestaoDeUsuarios.mudarMeuBalanco(novo)],
                          novo ? TEXTOS.MEU_BALANCO_LIGADO : TEXTOS.MEU_BALANCO_DESLIGADO,
                        );
                      else
                        executar(
                          [() => gestaoDeUsuarios.mudarBalancoDoVinculo(u.organizationUserId, novo)],
                          novo ? TEXTOS.BALANCO_LIGADO(u.nome) : TEXTOS.BALANCO_DESLIGADO(u.nome),
                        );
                    }}
                  />
                  {u.recebeBalanco && u.motivoSemEnvio && (
                    <Typography variant="caption">{TEXTOS.LIGADO_SEM_ENVIO(TEXTOS.MOTIVO[u.motivoSemEnvio])}</Typography>
                  )}
                </TableCell>
              )}
              {comAcoes && (
                <TableCell>
                  {podeAlcance(u) && (
                    <Button size="small" onClick={() => abrirAlcance(u)}>
                      Alcance
                    </Button>
                  )}
                  {editavel && (
                    <Button size="small" onClick={() => setModal({ tipo: "papeis", usuario: u })}>
                      Papéis
                    </Button>
                  )}
                  {editavel && (
                    <Button size="small" onClick={() => setModal({ tipo: "remover", usuario: u })}>
                      Remover desta organização
                    </Button>
                  )}
                  {editavel &&
                    u.podeSuspender &&
                    (u.suspenso ? (
                      <Button
                        size="small"
                        onClick={() =>
                          executar([() => gestaoDeUsuarios.reativar(u.userId)], `Acesso de ${u.nome} reativado.`)
                        }
                      >
                        Reativar
                      </Button>
                    ) : (
                      <Button size="small" onClick={() => setModal({ tipo: "suspender", usuario: u })}>
                        Suspender
                      </Button>
                    ))}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {pendencias.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6">{TEXTOS.PENDENCIAS}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {TEXTOS.PENDENCIAS_EXPLICA}
          </Typography>
          <Table size="small" aria-label={`${TEXTOS.PENDENCIAS} de ${organizacaoNome}`}>
            <TableHead>
              <TableRow>
                <TableCell>Pessoa</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell />
                {pendencias.some((p) => podeAlcance(p.u)) && <TableCell>Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {pendencias.map(({ u, organizationId: org, alcance }) => {
                const empresa = carteiraLida?.find((e) => e.organizationId === org);
                // why: a carteira é lida à parte; se a empresa ainda não está nela, mostra o id curto e o backend decide.
                const nomeDaEmpresa = empresa?.nome ?? org.slice(0, 8);
                return (
                  <TableRow key={`${u.organizationUserId}:${org}`}>
                    <TableCell>{u.nome}</TableCell>
                    <TableCell>{nomeDaEmpresa}</TableCell>
                    <TableCell>{empresa ? formatCnpj(empresa.cnpj) : ""}</TableCell>
                    <TableCell>
                      {empresa && carteiraLida && raizInteira(alcance, empresa, carteiraLida) && (
                        <Chip size="small" color="info" label={TEXTOS.RAIZ_INTEIRA} />
                      )}
                    </TableCell>
                    {pendencias.some((p) => podeAlcance(p.u)) && (
                      <TableCell>
                        {podeAlcance(u) && (
                          <>
                            <Button
                              size="small"
                              disabled={ocupado || !carteiraLida}
                              onClick={() =>
                                executar(
                                  [() => gestaoDeUsuarios.decidirEmpresa(u.organizationUserId, org, true)],
                                  `${nomeDaEmpresa} incluída no alcance de ${u.nome}.`,
                                )
                              }
                            >
                              Incluir
                            </Button>
                            <Button
                              size="small"
                              disabled={ocupado || !carteiraLida}
                              onClick={() =>
                                executar(
                                  [() => gestaoDeUsuarios.decidirEmpresa(u.organizationUserId, org, false)],
                                  `${nomeDaEmpresa} fica fora do alcance de ${u.nome}.`,
                                )
                              }
                            >
                              Não incluir
                            </Button>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {convitesComErro && (
        <Alert severity="warning" sx={{ mt: 4 }}>
          {TEXTOS.ERRO_AO_CARREGAR_CONVITES}
        </Alert>
      )}
      {!!convites?.length && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6">{TEXTOS.CONVITES_PENDENTES}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {TEXTOS.CONVITES_EXPLICA}
          </Typography>
          <Table size="small" aria-label={`${TEXTOS.CONVITES_PENDENTES} de ${organizacaoNome}`}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Papéis</TableCell>
                <TableCell>Situação</TableCell>
                {lado === "CORRETORA" && <TableCell>{TEXTOS.ALCANCE}</TableCell>}
                {comAcoesDeConvite && <TableCell>Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {convites.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.nome}</TableCell>
                  <TableCell>{c.email}</TableCell>
                  <TableCell>
                    <Chip size="small" label={NOME_DO_PAPEL.CONSULTA} sx={{ mr: 0.5 }} />
                    {c.papeis
                      .filter((p) => !p.endsWith("_CONSULTA"))
                      .map((p) => (
                        <Chip key={p} size="small" color="primary" label={papelLegivel(p)} sx={{ mr: 0.5 }} />
                      ))}
                  </TableCell>
                  <TableCell>
                    {c.jaTemConta ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={editavel ? TEXTOS.JA_TEM_CONTA_CANCELE : TEXTOS.JA_TEM_CONTA}
                      />
                    ) : c.vencido ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={editavel ? TEXTOS.CONVITE_VENCIDO_REENVIE : TEXTOS.CONVITE_VENCIDO}
                      />
                    ) : (
                      c.enviadoEm && TEXTOS.ENVIADO_EM(c.enviadoEm)
                    )}
                  </TableCell>
                  {lado === "CORRETORA" && (
                    <TableCell>
                      {c.alcance === "SELECAO" ? TEXTOS.N_EMPRESAS(c.selecao.length) : TEXTOS.TODA_A_CARTEIRA}
                    </TableCell>
                  )}
                  {comAcoesDeConvite && (
                    <TableCell>
                      {podeAlcanceDeConvite && (
                        <Button size="small" onClick={() => abrirAlcanceDoConvite(c)}>
                          {TEXTOS.ALCANCE}
                        </Button>
                      )}
                      {editavel && (
                        <Button size="small" onClick={() => setModal({ tipo: "papeisDoConvite", convite: c })}>
                          Papéis
                        </Button>
                      )}
                      {/* why: a quem já tem conta o link de criar conta só daria erro (revisão F1). */}
                      {editavel && !c.jaTemConta && (
                        <Button
                          size="small"
                          disabled={ocupado}
                          onClick={() =>
                            executar(
                              [() => gestaoDeUsuarios.reenviarConvite(organizationId, c.id)],
                              `Convite reenviado para ${c.email}.`,
                            )
                          }
                        >
                          Reenviar
                        </Button>
                      )}
                      {editavel && (
                        <Button size="small" onClick={() => setModal({ tipo: "cancelarConvite", convite: c })}>
                          Cancelar convite
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {modal?.tipo === "alcance" && (
        <ModalDeAlcance
          key={modal.usuario.organizationUserId}
          aberto
          ocupado={ocupado}
          usuario={modal.usuario}
          atual={modal.atual}
          carteira={modal.carteira}
          onFechar={() => setModal(null)}
          onSalvar={(pedido) =>
            executar(
              [() => gestaoDeUsuarios.definirAlcance(modal.usuario.organizationUserId, pedido)],
              `Alcance de ${modal.usuario.nome} atualizado.`,
            )
          }
        />
      )}
      {modal?.tipo === "alcanceDoConvite" && (
        <ModalDeAlcance
          key={modal.convite.id}
          aberto
          ocupado={ocupado}
          usuario={{ nome: modal.convite.nome }}
          atual={modal.atual}
          carteira={modal.carteira}
          onFechar={() => setModal(null)}
          onSalvar={(pedido) =>
            executar(
              [() => gestaoDeUsuarios.mudarAlcanceDoConvite(organizationId, modal.convite.id, pedido)],
              TEXTOS.ALCANCE_DO_CONVITE(modal.convite.nome),
            )
          }
        />
      )}
      {modal?.tipo === "papeisDoConvite" && (
        <ModalDePapeis
          aberto
          ocupado={ocupado}
          usuario={modal.convite}
          lado={lado}
          onFechar={() => setModal(null)}
          onSalvar={({ dar, tirar }) =>
            executar(
              [
                () =>
                  gestaoDeUsuarios.mudarPapeisDoConvite(organizationId, modal.convite.id, [
                    ...modal.convite.papeis.filter((p) => !tirar.includes(p)),
                    ...dar,
                  ]),
              ],
              `Papéis do convite de ${modal.convite.nome} atualizados. Valem quando a pessoa aceitar.`,
            )
          }
        />
      )}
      {modal?.tipo === "cancelarConvite" && (
        <ModalDeConfirmacao
          aberto
          ocupado={ocupado}
          titulo="Cancelar convite"
          texto={`O link enviado para ${modal.convite.email} deixa de valer. Para dar acesso depois, convide de novo.`}
          acao="Cancelar convite"
          desistir="Manter convite"
          onFechar={() => setModal(null)}
          onConfirmar={() =>
            executar(
              [() => gestaoDeUsuarios.cancelarConvite(organizationId, modal.convite.id)],
              `Convite de ${modal.convite.nome} cancelado.`,
            )
          }
        />
      )}
      {modal?.tipo === "convite" && (
        <ModalDeConvite
          aberto
          ocupado={ocupado}
          organizationId={organizationId}
          podeAlcance={podeAlcanceDeConvite}
          carteira={lerCarteira}
          onFechar={() => setModal(null)}
          onConvidar={(c) => {
            let mensagem: string = TEXTOS.CONVITE_ENVIADO;
            executar(
              [
                async () => {
                  const r = await gestaoDeUsuarios.convidar(organizationId, {
                    ...c,
                    papeis: c.papeis.map((p) => `${lado}_${p}`),
                  });
                  if (r?.resultado === "VINCULADO")
                    mensagem = r.avisoEnviado === false ? TEXTOS.ACESSO_DADO_SEM_AVISO : TEXTOS.ACESSO_DADO_COM_AVISO;
                },
              ],
              () => mensagem,
            );
          }}
        />
      )}
      {modal?.tipo === "revisao" && (
        <ModalDeRevisao
          aberto
          ocupado={ocupado}
          usuarios={usuarios}
          lado={lado}
          onFechar={() => setModal(null)}
          onConfirmar={(gestores) =>
            executar([() => gestaoDeUsuarios.revisar(organizationId, gestores)], "Administradores confirmados.")
          }
        />
      )}
      {modal?.tipo === "papeis" && (
        <ModalDePapeis
          aberto
          ocupado={ocupado}
          usuario={modal.usuario}
          lado={lado}
          onFechar={() => setModal(null)}
          onSalvar={({ dar, tirar }) =>
            executar(
              [
                ...tirar.map((p) => () => gestaoDeUsuarios.removerPapel(modal.usuario.organizationUserId, p)),
                ...dar.map((p) => () => gestaoDeUsuarios.adicionarPapel(modal.usuario.organizationUserId, p)),
              ],
              `Papéis de ${modal.usuario.nome} atualizados.`,
            )
          }
        />
      )}
      {modal?.tipo === "remover" && (
        <ModalDeConfirmacao
          aberto
          ocupado={ocupado}
          titulo="Remover desta organização"
          texto={`${modal.usuario.nome} perde o acesso a ${organizacaoNome}. A conta continua a existir.`}
          acao="Remover"
          onFechar={() => setModal(null)}
          onConfirmar={() =>
            executar(
              [() => gestaoDeUsuarios.remover(modal.usuario.organizationUserId)],
              `${modal.usuario.nome} não tem mais acesso a ${organizacaoNome}.`,
            )
          }
        />
      )}
      {modal?.tipo === "suspender" && (
        <ModalDeConfirmacao
          aberto
          ocupado={ocupado}
          titulo="Suspender acesso"
          texto={`${modal.usuario.nome} não consegue mais entrar na plataforma até ser reativado.`}
          acao="Suspender"
          onFechar={() => setModal(null)}
          onConfirmar={() =>
            executar(
              [() => gestaoDeUsuarios.suspender(modal.usuario.userId)],
              `Acesso de ${modal.usuario.nome} suspenso.`,
            )
          }
        />
      )}
    </Box>
  );
}
